import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, InputAdornment } from '@material-ui/core';
import { StyledTableRow } from '../../util/customFunctions';
import NumberFormat from "react-number-format";
import * as d3 from "d3";
import CopyTablesObjectv2 from "../../components/xlsx-export/CopyTablesObjectv2";

const useStyles = makeStyles({
  summaryContainer: {
    margin: '5px 0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '1rem',
    borderRadius: 0,
  },
  headerCell: {
    backgroundColor: '#4471c4',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '14px',
    padding: '12px',
    border: '0.5px solid #ddd',
    width: '100px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  labelCell: {
    fontWeight: 'bold',
    padding: '5px 8px',
    border: '0.5px solid #ddd',
  },
  dataCell: {
    padding: '5px 8px',
    textAlign: 'right',
    border: '0.5px solid #ddd',
  },
  totalRow: {
    fontWeight: 'bold',
  },
  editableCell: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  textField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: "28px",
      padding: 0,
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px",
      fontSize: "0.85rem",
    },
    "& .MuiInputAdornment-root": {
      marginRight: "4px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
});

function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: { name: props.name, value: values.value },
        });
      }}
      thousandSeparator
      isNumericString
      allowNegative={true}
    />
  );
}

const ForecastSummaryTable = ({ 
  forecastData, 
  judgementValues, 
  onJudgementChange, 
  includeForForecastRollup, 
  repTotals, 
  wonData, 
  quotaData, 
  isEdit,
  quotaLabel,
  currencySymbol = "$" 
}) => {
  const classes = useStyles();
  const tableRef = useRef();
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState("");

  const formatCurrency = (value) => {
    return `${currencySymbol}${d3.format(",")(Math.round(value))}`;
  };

  // Calculate Already Won for each category
  const getAlreadyWon = (categoryName) => {
    return wonData
      .filter(f => f.Forecast_Category_Name === categoryName)
      .reduce((sum, item) => sum + (parseFloat(item?.acv) || 0), 0);
  };

  // Get Rep Category Total (From specific opportunities)
  const getRepCategoryTotal = (categoryName) => {
    return parseFloat(repTotals?.[categoryName] || 0);
  };

  // Get Judgement value for category
  const getJudgementValue = (categoryName) => {
    return parseFloat(judgementValues?.[categoryName] || 0);
  };

  // Calculate Total for each category
  const getCategoryTotal = (categoryName) => {
    return getAlreadyWon(categoryName) + getRepCategoryTotal(categoryName) + getJudgementValue(categoryName);
  };

  // Calculate Best Case Total (Commit + Best Case Incremental)
  const getBestCaseTotal = (rowType) => {
    if (includeForForecastRollup.length < 2) return 0;
    
    const commitCategory = includeForForecastRollup[0]; // First category (Commit)
    const bestCaseCategory = includeForForecastRollup[1]; // Second category (Best Case)
    
    switch(rowType) {
      case 'alreadyWon':
        return getAlreadyWon(commitCategory) + getAlreadyWon(bestCaseCategory);
      case 'opportunities':
        return getRepCategoryTotal(commitCategory) + getRepCategoryTotal(bestCaseCategory);
      case 'judgement':
        return getJudgementValue(commitCategory) + getJudgementValue(bestCaseCategory);
      case 'total':
        return getCategoryTotal(commitCategory) + getCategoryTotal(bestCaseCategory);
      default:
        return 0;
    }
  };

  // Get Quota
  const quota = parseFloat(quotaData.reduce((a, b) => a + b.quota, 0) || 0);

  // Calculate % of Quota for each category
  const getPercentOfQuota = (categoryName) => {
    const total = getCategoryTotal(categoryName);
    return ((total / quota) * 100).toFixed(0);
  };

  // Calculate % of Quota for Best Case Total
  const getBestCaseTotalPercent = () => {
    const total = getBestCaseTotal('total');
    return ((total / quota) * 100).toFixed(0);
  };

  const handleDoubleClick = (categoryName, currentValue) => {
    setEditingField(categoryName);
    setTempValue(currentValue || "");
  };

  const handleBlur = () => {
    if (editingField) {
      onJudgementChange(editingField, tempValue);
    }
    setEditingField(null);
    setTempValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleBlur();
    }
  };

  const renderEditableCell = (categoryName) => {
    const isEditing = editingField === categoryName;
    const value = getJudgementValue(categoryName);
    
    return (
      <TableCell 
        className={`${classes.dataCell} ${!isEditing ? classes.editableCell : ""}`}
        onDoubleClick={() => handleDoubleClick(categoryName, value)}
      >
        {(isEditing && isEdit) ? (
          <TextField
            type="text"
            size="small"
            variant="outlined"
            autoFocus
            className={classes.textField}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
              inputComponent: NumberFormatCustom,
            }}
            placeholder="0"
          />
        ) : (
          formatCurrency(value)
        )}
      </TableCell>
    );
  };

  return (
    <>
      {/* Copy Button */}
      <div style={{ textAlign: 'right', marginBottom: '8px' }}>
        <CopyTablesObjectv2 tableRef={tableRef} />
      </div>

      <TableContainer 
        component={Paper} 
        className={classes.summaryContainer}
        style={{ 
          maxHeight: '60vh', 
          border: '0.5px solid #ddd',
        }}
      >
        <Table ref={tableRef} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell} style={{width: '150px'}}></TableCell>
              {includeForForecastRollup.map((category, index) => (
                <TableCell key={index} className={classes.headerCell} align="center" style={{background: category === 'Best Case' ? '#5b9bd5' : '#4471c4'}}>
                  {category === 'Best Case' ? `${category} Incremental` : category}
                </TableCell>
              ))}
              <TableCell key="best-case-total" className={classes.headerCell} align="center">
                Best Case Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Already Won Row */}
            <StyledTableRow>
              <TableCell className={classes.labelCell}>Already Won</TableCell>
              {includeForForecastRollup.map((category, index) => (
                <TableCell key={index} className={classes.dataCell}>
                  {formatCurrency(getAlreadyWon(category))}
                </TableCell>
              ))}
              <TableCell className={classes.dataCell}>
                {formatCurrency(getBestCaseTotal('alreadyWon'))}
              </TableCell>
            </StyledTableRow>
            
            {/* From specific opportunities Row */}
            <StyledTableRow>
              <TableCell className={classes.labelCell}>From specific opportunities</TableCell>
              {includeForForecastRollup.map((category, index) => (
                <TableCell key={index} className={classes.dataCell}>
                  {formatCurrency(getRepCategoryTotal(category))}
                </TableCell>
              ))}
              <TableCell className={classes.dataCell}>
                {formatCurrency(getBestCaseTotal('opportunities'))}
              </TableCell>
            </StyledTableRow>
            
            {/* Judgement Row (Editable) */}
            <StyledTableRow>
              <TableCell className={classes.labelCell}>Judgement</TableCell>
              {includeForForecastRollup.map((category, index) => (
                <React.Fragment key={index}>
                  {renderEditableCell(category)}
                </React.Fragment>
              ))}
              <TableCell className={classes.dataCell}>
                {formatCurrency(getBestCaseTotal('judgement'))}
              </TableCell>
            </StyledTableRow>
            
            {/* Total Row */}
            <StyledTableRow className={classes.totalRow}>
              <TableCell className={classes.labelCell}>Forecast Total</TableCell>
              {includeForForecastRollup.map((category, index) => (
                <TableCell key={index} className={classes.dataCell} style={{fontSize: '1.3rem', fontWeight: 600}}>
                  {formatCurrency(getCategoryTotal(category))}
                </TableCell>
              ))}
              <TableCell className={classes.dataCell} style={{fontSize: '1.3rem', fontWeight: 600}}>
                {formatCurrency(getBestCaseTotal('total'))}
              </TableCell>
            </StyledTableRow>

            {/* Quota Row */}
            <StyledTableRow>
              <TableCell className={classes.labelCell}>{quotaLabel ? quotaLabel : 'Quota'}</TableCell>
              <TableCell className={classes.dataCell}>
                {formatCurrency(quota)}
              </TableCell>
              <TableCell className={classes.dataCell}>
                
              </TableCell>
              <TableCell className={classes.dataCell}>
                {formatCurrency(quota)}
              </TableCell>
            </StyledTableRow>
            
            {/* % of Quota Row */}
            <StyledTableRow>
              <TableCell className={classes.labelCell}>% of Quota</TableCell>
              {includeForForecastRollup.filter(f => f !== 'Best Case').map((category, index) => (
                <TableCell key={index} className={classes.dataCell}>
                  {getPercentOfQuota(category)}%
                </TableCell>
              ))}
              <TableCell className={classes.dataCell}>
                
              </TableCell>
              <TableCell className={classes.dataCell}>
                {getBestCaseTotalPercent()}%
              </TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ForecastSummaryTable;